import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import messageService, { ScheduledMessage } from '../services/messageService';

interface MessageContent {
  content_type: string;
  content: string | any[];
}

const ScheduledMessagesList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ScheduledMessage[]>([]);
  const [searchPhone, setSearchPhone] = useState('');

  const loadMessages = async (phoneNumber?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await messageService.getScheduledMessages(phoneNumber);
      setMessages(response.results);
    } catch (err) {
      setError('Failed to load scheduled messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const handleSearch = () => {
    if (searchPhone) {
      // Remove any non-numeric characters and leading '+'
      const cleanPhone = searchPhone.replace(/\D/g, '').replace(/^\+/, '');
      loadMessages(cleanPhone);
    } else {
      loadMessages();
    }
  };

  const handleRefresh = () => {
    loadMessages(searchPhone || undefined);
  };

  const getMessageText = (msgData: string): string => {
    try {
      const contentArray = JSON.parse(msgData) as MessageContent[];
      const textContent = contentArray
        .filter(item => item.content_type === 'text' && typeof item.content === 'string')
        .map(item => item.content)
        .join(' ');
      return textContent || 'No text content';
    } catch (err) {
      console.error('Failed to parse message data:', err);
      return 'Invalid message format';
    }
  };

  if (loading && messages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search by Phone Number"
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Enter phone number..."
          sx={{ width: 250 }}
        />
        <IconButton onClick={handleSearch}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleRefresh} disabled={loading}>
          <RefreshIcon />
        </IconButton>
        {loading && <CircularProgress size={24} />}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Send To</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Send At</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    No scheduled messages found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow key={message.id}>
                  <TableCell>
                    <Tooltip title={message.contact_data ? JSON.stringify(message.contact_data, null, 2) : 'No contact data'}>
                      <span>{message.send_to}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.channel_type}
                      size="small"
                      color={message.channel_type === 'sms' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={message.msg_data}>
                      <Typography noWrap sx={{ maxWidth: 300 }}>
                        {getMessageText(message.msg_data)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {format(new Date(message.send_at), 'MMM d, yyyy h:mm a')}
                  </TableCell>
                  <TableCell>
                    {format(new Date(message.created_dt), 'MMM d, yyyy h:mm a')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.was_sent ? 'Sent' : 'Scheduled'}
                      color={message.was_sent ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ScheduledMessagesList; 